import React from 'react';
import { FiServer, FiBox, FiLayers, FiDatabase } from 'react-icons/fi';
import { IconType } from 'react-icons';

interface ServiceSaving {
  service: string;
  instance_count?: number | null;
  current_cost?: number | null;
  savings?: number | null;
}

interface SavingsByServiceCardsProps {
  savings: ServiceSaving[];
}

const SavingsByServiceCards: React.FC<SavingsByServiceCardsProps> = ({ savings }) => {
  const formatCurrency = (amount: number | null | undefined): string => {
    if (amount == null) return '$0';
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(amount);
  };

  const formatNumber = (num: number | null | undefined): string => {
    if (num == null) return '0';
    return new Intl.NumberFormat('en-US').format(num);
  };

  const getServiceIcon = (service: string): IconType => {
    const icons: Record<string, IconType> = {
      EC2: FiServer,
      ECS: FiBox,
      EKS: FiLayers,
      RDS: FiDatabase,
      SageMaker: FiServer, // Default icon
    };
    return icons[service] || FiServer;
  };

  const getServiceColor = (service: string): string => {
    const colors: Record<string, string> = {
      EC2: 'text-blue-600 dark:text-blue-400',
      ECS: 'text-blue-600 dark:text-blue-400',
      EKS: 'text-blue-600 dark:text-blue-400',
      RDS: 'text-blue-600 dark:text-blue-400',
      SageMaker: 'text-blue-600 dark:text-blue-400',
    };
    return colors[service] || 'text-blue-600 dark:text-blue-400';
  };

  return (
    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
      {savings.map((service) => {
        const Icon = getServiceIcon(service.service);
        const iconColor = getServiceColor(service.service);
        
        return (
          <div
            key={service.service}
            className="bg-white dark:bg-gray-800 shadow rounded-lg p-6 border border-gray-200 dark:border-gray-700"
          >
            <div className="flex items-start">
              <div className="flex-shrink-0 bg-blue-100 dark:bg-blue-900/50 rounded-lg p-3">
                <Icon className={`h-6 w-6 ${iconColor}`} />
              </div>
              <div className="ml-4 flex-1">
                <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-3">
                  {service.service}
                </h3>
                <div className="space-y-2">
                  <p className="text-sm text-gray-600 dark:text-gray-400">
                    {formatNumber(service.instance_count)} instances
                  </p>
                  <p className="text-sm text-gray-600 dark:text-gray-400">
                    {formatCurrency(service.current_cost)}/mo current
                  </p>
                  <p className="text-sm font-medium text-green-600 dark:text-green-400">
                    {formatCurrency(service.savings)}/mo savings
                  </p>
                </div>
              </div>
            </div>
          </div>
        );
      })}
    </div>
  );
};

export default SavingsByServiceCards;
